/**
 * toExportMetadata — Adapter processing.DocumentMetadata → export.types.DocumentMetadata
 *
 * Convierte los metadatos del pipeline de extracción (Step1/Step2) al formato
 * requerido por Step3Page V2 y ExportService.
 *
 * @version 1.0.0 — F5.04d
 */

import type { DocumentMetadata as ProcessingMetadata } from '@/types/processing';
import type { DocumentMetadata as ExportMetadata } from '@/types/export.types';

/**
 * FECHAREC: la fecha puede llegar como texto si los metadatos vienen de un
 * almacenamiento (caché del wizard). Se convierte aquí, única puerta a los
 * exportadores. Ilegible o ausente: fecha del momento de exportar, con aviso
 * (DOP-2 a′) para que el valor de reserva no pase callado.
 */
function aFecha(valor: unknown): Date {
  if (valor instanceof Date && !Number.isNaN(valor.getTime())) return valor;
  if (typeof valor === 'string' || typeof valor === 'number') {
    const fecha = new Date(valor);
    if (!Number.isNaN(fecha.getTime())) return fecha;
  }
  console.warn(
    '[toExportMetadata] Fecha de procesamiento ilegible o ausente; se usa la del momento de exportar:',
    valor,
  );
  return new Date();
}

/**
 * Convierte metadatos de procesamiento a metadatos de exportación.
 *
 * Campos required que pueden ser undefined en origin se rellenan con
 * defaults seguros para que el usuario identifique que faltan datos.
 */
export function toExportMetadata(meta: ProcessingMetadata): ExportMetadata {
  return {
    archivoOrigen: meta.fileName,
    fechaProcesamiento: aFecha(meta.processedAt),
    municipio: meta.municipality ?? 'Sin municipio',
    // PRV2: `||` (no `??`) — detectProvince devuelve '' cuando no puede
    // derivar la provincia (nunca inventa); '' debe caer al default legible.
    provincia: meta.province || 'Sin provincia',
    codigoINE: meta.ineCode ?? '00000',
    sistemaOrigen: meta.ed50Detection?.detected
      ? 'ED50 (EPSG:23030)'
      : 'ETRS89 (EPSG:25830)',
    sistemaDestino: 'UTM30 ETRS89 (EPSG:25830)',
  };
}
